import { useState, useEffect, useRef } from 'react';
import { differenceInMilliseconds } from 'date-fns';

export type AlertLevel = 'CRITICAL' | 'URGENT' | 'EXPIRING' | 'NORMAL';

export interface CountdownResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  totalHoursRemaining: number;
  totalMinutesRemaining: number;
  alertLevel: AlertLevel;
}

interface UseCountdownOptions {
  onExpire?: () => void;
  updateInterval?: number; // milliseconds, default 1000
}

/**
 * Custom hook for real-time countdown timer with alert level detection
 *
 * @param targetDate - The date to count down to
 * @param options - Optional configuration
 * @returns CountdownResult with time remaining and alert level
 *
 * @example
 * const countdown = useCountdown(freeTimeExpiry, {
 *   onExpire: () => console.log('Free time expired!'),
 *   updateInterval: 1000
 * });
 *
 * // Display: {countdown.days}d {countdown.hours}h {countdown.minutes}m
 * // Alert: {countdown.alertLevel} (CRITICAL/URGENT/EXPIRING/NORMAL)
 */
export function useCountdown(
  targetDate: Date | null,
  options: UseCountdownOptions = {}
): CountdownResult {
  const { onExpire, updateInterval = 1000 } = options;

  // Use refs to store callback and avoid dependency issues
  const expireCallbackFiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);

  // Update ref when callback changes
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Calculate initial countdown
  const calculateCountdown = (): CountdownResult => {
    // Handle null or invalid date
    if (!targetDate || !(targetDate instanceof Date) || isNaN(targetDate.getTime())) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: false,
        totalHoursRemaining: 0,
        totalMinutesRemaining: 0,
        alertLevel: 'NORMAL',
      };
    }

    const now = new Date();
    const diffMs = differenceInMilliseconds(targetDate, now);

    // Check if expired
    if (diffMs <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
        totalHoursRemaining: 0,
        totalMinutesRemaining: 0,
        alertLevel: 'CRITICAL',
      };
    }

    // Calculate time units
    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    // Determine alert level based on total hours remaining
    let alertLevel: AlertLevel = 'NORMAL';
    if (totalHours < 6) {
      alertLevel = 'CRITICAL';
    } else if (totalHours < 12) {
      alertLevel = 'URGENT';
    } else if (totalHours < 24) {
      alertLevel = 'EXPIRING';
    }

    return {
      days,
      hours,
      minutes,
      seconds,
      isExpired: false,
      totalHoursRemaining: totalHours,
      totalMinutesRemaining: totalMinutes,
      alertLevel,
    };
  };

  // State for countdown values
  const [countdown, setCountdown] = useState<CountdownResult>(calculateCountdown);

  useEffect(() => {
    // Reset expire callback flag when targetDate changes
    expireCallbackFiredRef.current = false;

    // Update countdown immediately when targetDate changes
    setCountdown(calculateCountdown());

    // Set up interval for countdown updates
    const interval = setInterval(() => {
      const newCountdown = calculateCountdown();
      setCountdown(newCountdown);

      // Fire onExpire callback once when countdown expires
      if (newCountdown.isExpired && onExpireRef.current && !expireCallbackFiredRef.current) {
        expireCallbackFiredRef.current = true;
        onExpireRef.current();
      }
    }, updateInterval);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate, updateInterval]);

  return countdown;
}
